// The resolution switch (2026-10-27, docs/agent-draft-shadow.md "Switching
// between replacement and refund"; docs/contact-view.md "Replacement or
// refund on the step"; docs/decisions/2026-10-27-agent-resolution-switch.md).
//
// On the agent layout (the shadow panel, and the main panel when the agent's
// draft is the main one), a replacement or refund step can be switched to
// the other resolution on the step itself. Both paths are built up front by
// the pipeline's own builders. On the main panel it is a task_path_choice
// step (kind 'resolution') with a preselection, and every step of a path
// carries its taskPath ('replacement', 'refund' or 'split'), the sales-hold
// path choice's pattern: only the chosen path's steps show, Process and
// Process all. Pure: no calls.

import { effectiveTaskPathChoice, effectiveTaskPathKey, taskPathChoiceStep, taskPathChoiceFrom } from './tasks.js'

export const RESOLUTION_KIND = 'resolution'
export const RESOLUTION_REPLACEMENT = 'replacement'
export const RESOLUTION_REFUND = 'refund'
// A replacement for the parts in stock and a refund for the ones that are not.
export const RESOLUTION_SPLIT = 'split'
export const RESOLUTION_PATHS = Object.freeze([RESOLUTION_REPLACEMENT, RESOLUTION_REFUND, RESOLUTION_SPLIT])
export const RESOLUTION_LABELS = Object.freeze({
  [RESOLUTION_REPLACEMENT]: 'Replacement',
  [RESOLUTION_REFUND]: 'Refund',
  [RESOLUTION_SPLIT]: 'Refund the out-of-stock part(s), replace the rest',
})
// Why a person switched: a part out of stock, or their own choice.
export const RESOLUTION_WHY_OUT_OF_STOCK = 'out_of_stock'
export const RESOLUTION_WHY_CSR_CHOICE = 'csr_choice'
export const RESOLUTION_WHYS = Object.freeze([RESOLUTION_WHY_OUT_OF_STOCK, RESOLUTION_WHY_CSR_CHOICE])
// What the customer asked for (the agent records it per resolution step).
export const CUSTOMER_CHOICES = Object.freeze(['replacement', 'refund', 'unspecified'])
export const SWITCH_TO_REFUND_LABEL = 'Switch to refund'
export const SWITCH_BACK_LABEL = 'Switch back to the replacement'
export const MAX_CUSTOMER_QUOTE_CHARS = 160

// The agent's customerChoice, cleaned: 'replacement' | 'refund' |
// 'unspecified'; anything else is unspecified.
export function cleanCustomerChoice(v) {
  const s = String(v ?? '').trim().toLowerCase()
  return CUSTOMER_CHOICES.includes(s) ? s : 'unspecified'
}

// The toggle shows only when the customer did not say which they want.
export function showsResolutionToggle(customerChoice) {
  return cleanCustomerChoice(customerChoice) === 'unspecified'
}

// The SKUs being replaced that are out of stock or short of the quantity:
// [{ sku, available, needed }]. stock: { [SKU]: { inStock, quantity } }
// (quantity null: unknown, never counted as out of stock). items: [{ sku,
// quantity }].
export function outOfStockItems(stock, items) {
  const out = []
  for (const it of Array.isArray(items) ? items : []) {
    const sku = String(it?.sku ?? '').trim().toUpperCase()
    if (!sku) continue
    const s = stock?.[sku] ?? stock?.[it.sku] ?? null
    const q = s?.quantity
    if (q == null || !Number.isFinite(Number(q))) continue
    const available = Math.max(0, Number(q))
    const needed = Math.max(1, Number(it.quantity) || 1)
    if (available < needed) out.push({ sku, available, needed })
  }
  return out
}

// The warning line for one short SKU: "<SKU> is out of stock (0
// available)." or, short of the quantity, "<SKU> is short (1 available, 2
// needed)."
export function outOfStockLine({ sku, available, needed }) {
  if (available <= 0) return `${sku} is out of stock (0 available).`
  return `${sku} is short (${available} available, ${needed} needed).`
}

// The items split by stock: { inStock: [{ sku, quantity }], outOfStock:
// [{ sku, quantity }] }. A short SKU counts as out of stock (its whole
// quantity is refunded).
export function splitByStock(stock, items) {
  const short = new Set(outOfStockItems(stock, items).map((x) => x.sku))
  const inStock = []
  const outOfStock = []
  for (const it of Array.isArray(items) ? items : []) {
    const sku = String(it?.sku ?? '').trim().toUpperCase()
    if (!sku) continue
    ;(short.has(sku) ? outOfStock : inStock).push({ sku: it.sku, quantity: Math.max(1, Number(it.quantity) || 1) })
  }
  return { inStock, outOfStock }
}

export function isResolutionChoice(step) {
  return step?.type === 'task_path_choice' && step?.params?.kind === RESOLUTION_KIND
}

// A reply step of a resolution path (never shown as a step: choosing the
// path loads its reply).
export function isResolutionReplyStep(step) {
  return step?.type === 'task_reply_template' && RESOLUTION_PATHS.includes(step?.params?.resolutionPath)
}

// The path in effect on a suggestion: { path, preselected, overridden, why }
// or null when it has no resolution choice.
export function resolutionOf(suggestion) {
  const step = taskPathChoiceStep(suggestion?.proposedActions)
  if (!isResolutionChoice(step)) return null
  const preselected = step.params?.preselected?.value ?? null
  const effective = effectiveTaskPathChoice(suggestion)
  const path = effective?.choice ?? preselected
  const recorded = taskPathChoiceFrom(suggestion?.modelMeta?.apiCalls)
  const why = recorded ? resolutionWhyFrom(suggestion?.modelMeta?.apiCalls) : null
  return { path, preselected, overridden: Boolean(path && preselected && path !== preselected), why }
}

// The latest recorded reason for a switch.
export function resolutionWhyFrom(apiCalls) {
  const list = Array.isArray(apiCalls) ? apiCalls : []
  for (let k = list.length - 1; k >= 0; k--) {
    const c = list[k]
    if (c?.taskPathChoice) return RESOLUTION_WHYS.includes(c.resolutionWhy) ? c.resolutionWhy : null
  }
  return null
}

// The decision's record of the resolution (the Audit agreement and the
// feedback snapshot read it): resolution 'replacement' | 'refund' | 'split',
// whether it was the preselected one and why it was switched. null when
// there is no resolution choice.
export function resolutionDecisionEntry({ suggestion, status = null, actedBy = null, at = new Date().toISOString() } = {}) {
  const r = resolutionOf(suggestion)
  if (!r?.path) return null
  const why = r.overridden ? (r.why ?? RESOLUTION_WHY_CSR_CHOICE) : null
  // A refund chosen on an order outside the return window (2026-10-27).
  const step = taskPathChoiceStep(suggestion?.proposedActions)
  const outsideWindow = step?.params?.outsideWindow === true && (r.path === RESOLUTION_REFUND || r.path === RESOLUTION_SPLIT)
  return {
    service: 'CXHub', method: 'DECISION', phase: 'approve', at, actedBy,
    label: `Decided (${status ?? 'unknown'}) on the resolution: ${RESOLUTION_LABELS[r.path] ?? r.path}${r.overridden ? ` (switched from ${RESOLUTION_LABELS[r.preselected] ?? r.preselected}: ${why === RESOLUTION_WHY_OUT_OF_STOCK ? 'out of stock' : 'CSR choice'})` : ' (as proposed)'}${outsideWindow ? ', outside the 60-day return window' : ''}`,
    resolution: r.path,
    resolutionPreselected: !r.overridden,
    ...(why ? { resolutionWhy: why } : {}),
    ...(outsideWindow ? { resolutionOutsideWindow: true } : {}),
  }
}

// The recorded decision's resolution: { path, preselected, why } or null.
export function resolutionFrom(apiCalls) {
  const list = Array.isArray(apiCalls) ? apiCalls : []
  for (let k = list.length - 1; k >= 0; k--) {
    const c = list[k]
    if (RESOLUTION_PATHS.includes(c?.resolution)) {
      return { path: c.resolution, preselected: c.resolutionPreselected === true, why: RESOLUTION_WHYS.includes(c.resolutionWhy) ? c.resolutionWhy : null, ...(c.resolutionOutsideWindow === true ? { outsideWindow: true } : {}) }
    }
  }
  return null
}

// The steps the chosen path does not run, as a non-processable stand-in
// (index kept), for the promise check at Send: a hidden path's step never
// counts as a step of the contact.
export function withoutOffPathSteps(proposedActions, apiCalls = []) {
  const list = Array.isArray(proposedActions) ? proposedActions : []
  if (!list.some((a) => a?.taskPath)) return list
  // The path key (2026-10-29): a delivered return past its refund window
  // runs the overdue path (tasks.js's effectiveTaskPathKey()).
  const choice = effectiveTaskPathKey({ modelMeta: { apiCalls }, proposedActions: list })
  return list.map((a) => (a?.taskPath && a.taskPath !== choice ? { type: 'off_path', label: a.label ?? null } : a))
}

// "Replace your reply with the <template> reply?"
export function resolutionReplyPrompt(title) {
  return `Replace your reply with the ${title ? `"${title}"` : 'new path\'s'} reply?`
}

// The step's note when the order is outside the return window (2026-10-27,
// Jenn): the refund is still offered, with this note, and the choice
// records it.
export const RESOLUTION_OUTSIDE_WINDOW_NOTE = 'Outside the 60-day return window.'

// Whether the step offers the Replacement | Refund toggle: the customer did
// not say which they want, or asked for the other remedy than the one
// staged (2026-10-27, Jenn: a refund asked for on a staged replacement is
// one click away). headPath: the staged path ('replacement' | 'refund').
export function offersResolutionToggle(customerChoice, headPath = null) {
  const c = cleanCustomerChoice(customerChoice)
  if (c === 'unspecified') return true
  return Boolean(headPath) && c !== headPath
}

// --- The split reply (2026-10-27, Jenn): one natural reply ---------------------
// The replacement template and the refund template meshed into one: one
// greeting (the replacement's), the replacement part, then the refund part,
// one closing and one signature (the lines both end with), and no line said
// twice (a Reference ID, an address block, a thank-you). Each template's
// own content and filled values stay; a placeholder or an RMA token stays
// where it was, so the send checks still see it. Plain text (blocks split by
// blank lines) or HTML (blocks split at paragraph ends), as the reply box
// holds it.
const GREETING_RE = /^(hi|hello|hey|dear|greetings|good (morning|afternoon|evening))\b/i
const CLOSING_RE = /^(thank you|thanks|sincerely|regards|best regards|kind regards|best wishes|best,|we appreciate your|we apologize for any inconvenience|please let us know if|if you have any (other |further )?questions|feel free to)/i
const REFERENCE_RE = /reference id\s*#?\s*:/i

const isHtml = (t) => /<(p|div|br)[\s>/]/i.test(String(t ?? ''))
export function replyBlocks(text) {
  const t = String(text ?? '')
  if (isHtml(t)) {
    return t.split(/(?<=<\/p>|<\/div>)/i).map((b) => b).filter((b) => b.trim())
  }
  return t.replace(/\r\n/g, '\n').split(/\n{2,}/).filter((b) => b.trim())
}
export function blockKey(block) {
  return String(block ?? '').replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').replace(/[^\p{L}\p{N}#:\[\]_]+/gu, ' ').trim().toLowerCase()
}
const keyText = (b) => blockKey(b)

export function meshReplyTexts(first, second) {
  const a = replyBlocks(first)
  const b = replyBlocks(second)
  if (!a.length) return String(second ?? '')
  if (!b.length) return String(first ?? '')
  // The tail both share (the signature and any closing they both carry).
  let n = 0
  while (n < a.length && n < b.length && keyText(a[a.length - 1 - n]) === keyText(b[b.length - 1 - n])) n += 1
  const suffix = a.slice(a.length - n)
  let bodyA = a.slice(0, a.length - n)
  let bodyB = b.slice(0, b.length - n)
  // One greeting: the first template's.
  if (bodyB.length && GREETING_RE.test(keyText(bodyB[0]))) bodyB = bodyB.slice(1)
  // One closing: the first template's closing lines go; the second's stay.
  const closesB = bodyB.some((x) => CLOSING_RE.test(keyText(x)))
  if (closesB) while (bodyA.length > 1 && CLOSING_RE.test(keyText(bodyA[bodyA.length - 1]))) bodyA = bodyA.slice(0, -1)
  // One Reference ID line: the refund's when it carries the RMA token (the
  // number only exists once the return is processed), else the first's.
  const refA = bodyA.findIndex((x) => REFERENCE_RE.test(keyText(x)))
  const refB = bodyB.findIndex((x) => REFERENCE_RE.test(keyText(x)))
  if (refA >= 0 && refB >= 0) {
    if (/\[\[RMA_REFERENCE\]\]/.test(bodyB[refB]) && !/\[\[RMA_REFERENCE\]\]/.test(bodyA[refA])) bodyA = bodyA.filter((_, i) => i !== refA)
    else bodyB = bodyB.filter((_, i) => i !== refB)
  }
  // No line said twice.
  const seen = new Set(bodyA.map(keyText))
  bodyB = bodyB.filter((x) => {
    const k = keyText(x)
    if (!k || seen.has(k)) return false
    seen.add(k)
    return true
  })
  const blocks = [...bodyA, ...bodyB, ...suffix]
  return isHtml(first) || isHtml(second) ? blocks.join('') : blocks.join('\n\n')
}
